import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { isPWAStandalone } from '../../lib/utils';
import { X, ChefHat, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNoshStore } from '../../lib/store';

// Realistic phase timing based on actual recipe generation performance
const PHASE_TIMING = {
  'initializing': {
    title: 'Preparing Request',
    icon: '⚡',
    expectedDuration: 2, // 2 seconds
    cumulativeTime: 2,
    progressWeight: 5
  },
  'analyzing-preferences': {
    title: 'Analyzing Preferences',
    icon: '🎯',
    expectedDuration: 3, // 3 seconds
    cumulativeTime: 5,
    progressWeight: 12
  },
  'checking-inventory': {
    title: 'Checking Inventory',
    icon: '📦',
    expectedDuration: 5, // 5 seconds
    cumulativeTime: 10,
    progressWeight: 20
  },
  'generating-recipe': {
    title: 'Creating Recipe',
    icon: '👨‍🍳',
    expectedDuration: 25, // 25 seconds (longest phase)
    cumulativeTime: 35,
    progressWeight: 70
  },
  'calculating-nutrition': {
    title: 'Calculating Nutrition',
    icon: '🧮',
    expectedDuration: 8, // 8 seconds
    cumulativeTime: 43,
    progressWeight: 85
  },
  'optimizing-instructions': {
    title: 'Optimizing Steps',
    icon: '📝',
    expectedDuration: 5, // 5 seconds
    cumulativeTime: 48,
    progressWeight: 92
  },
  'finalizing': {
    title: 'Finalizing Recipe',
    icon: '✨',
    expectedDuration: 7, // 7 seconds
    cumulativeTime: 55,
    progressWeight: 98
  },
  'complete': {
    title: 'Recipe Ready!',
    icon: '✅',
    expectedDuration: 0,
    cumulativeTime: 55,
    progressWeight: 100
  }
};

// Total expected duration for the entire process
const TOTAL_EXPECTED_DURATION = 55; // seconds

// Dynamic text messages that rotate during generation
const DYNAMIC_MESSAGES = [
  "Creating recipes...",
  "Cherry picking ingredients...", 
  "Cooking up something special...",
  "Removing allergic items...",
  "Seasoning to perfection...",
  "Adding the final touches...",
  "Plating your masterpiece...",
  "Balancing flavors...",
  "Crafting cooking steps...",
  "Mixing and matching...",
  "Spicing things up...",
  "Stirring the pot...",
  "Whisking up magic...",
  "Garnishing with love..."
];

export function GlobalRecipeGenerationModal() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isStandalone, setIsStandalone] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [showMessage, setShowMessage] = useState(true);
  const { 
    recipeGeneration, 
    cancelRecipeGeneration,
    hideRecipeGenerationModal
  } = useNoshStore();

  // Use global hasAnimated state
  const hasAnimated = recipeGeneration.hasAnimated;

  // Dynamic text rotation effect - use global elapsed time for consistency
  useEffect(() => {
    if (!recipeGeneration.isActive || !recipeGeneration.showModal) return;

    // Calculate message index based on elapsed time (changes every 2.5 seconds)
    const messageIndex = Math.floor(recipeGeneration.elapsedTime / 2.5) % DYNAMIC_MESSAGES.length;
    
    // Only update if index has changed to prevent unnecessary re-renders
    if (messageIndex !== currentMessageIndex) {
      setShowMessage(false);
      
      setTimeout(() => {
        setCurrentMessageIndex(messageIndex);
        setShowMessage(true);
      }, hasAnimated ? 0 : 150); // No delay if already animated
    }
  }, [recipeGeneration.elapsedTime, recipeGeneration.isActive, recipeGeneration.showModal, currentMessageIndex, hasAnimated]);

  // Reset message when modal opens - but only on first appearance
  useEffect(() => {
    if (recipeGeneration.showModal && !hasAnimated) {
      setCurrentMessageIndex(0);
      setShowMessage(true);
    }
  }, [recipeGeneration.showModal, hasAnimated]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Check if app is running in standalone mode
    const checkStandalone = () => {
      setIsStandalone(isPWAStandalone());
    };

    handleResize();
    checkStandalone();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Debug logging for mobile standalone mode - only log on modal open
  useEffect(() => {
    const isMobileStandalone = isMobile && isStandalone;
    if (isMobileStandalone && recipeGeneration.showModal) {
      console.log('🍳 [Global Recipe Generation Modal] Opening:', {
        isMobile,
        isStandalone,
        isMobileStandalone,
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight
      });
    }
  }, [recipeGeneration.showModal, isMobile, isStandalone]); // Only track modal open/close

  if (!recipeGeneration.showModal) return null;

  const currentPhase = recipeGeneration.currentPhase as keyof typeof PHASE_TIMING;
  const phaseInfo = PHASE_TIMING[currentPhase] || {
    title: 'Processing...',
    icon: '⏳',
    expectedDuration: 10,
    cumulativeTime: 30,
    progressWeight: 50
  };

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Realistic progress calculation based on phases and timing
  const calculateRealisticProgress = () => {
    if (!recipeGeneration.isActive) return Math.min(phaseInfo.progressWeight, 100);
    
    const elapsedSeconds = recipeGeneration.elapsedTime;
    
    // If we're past the expected total duration, gradually approach 98%
    if (elapsedSeconds > TOTAL_EXPECTED_DURATION) {
      const overtime = elapsedSeconds - TOTAL_EXPECTED_DURATION;
      const overtimeProgress = Math.min(overtime / 30, 1); // Approach 98% over 30 seconds of overtime
      return Math.min(95 + (overtimeProgress * 3), 98); // 95% to 98% max
    }
    
    // Calculate progress within current phase
    const previousPhases = Object.keys(PHASE_TIMING).slice(0, Object.keys(PHASE_TIMING).indexOf(currentPhase));
    const previousProgress = previousPhases.reduce((sum, phase) => {
      return sum + PHASE_TIMING[phase as keyof typeof PHASE_TIMING].progressWeight;
    }, 0) - (previousPhases.length > 0 ? PHASE_TIMING[previousPhases[previousPhases.length - 1] as keyof typeof PHASE_TIMING].progressWeight : 0);
    
    // Calculate time spent in current phase
    const phaseStartTime = previousPhases.reduce((sum, phase) => {
      return sum + PHASE_TIMING[phase as keyof typeof PHASE_TIMING].expectedDuration;
    }, 0);
    
    const timeInCurrentPhase = Math.max(0, elapsedSeconds - phaseStartTime);
    const phaseProgress = Math.min(timeInCurrentPhase / phaseInfo.expectedDuration, 1);
    
    // Calculate weighted progress within current phase
    const currentPhaseWeight = phaseInfo.progressWeight - previousProgress;
    const progressInPhase = phaseProgress * currentPhaseWeight;
    
    const totalProgress = previousProgress + progressInPhase;
    
    // Ensure we never exceed the phase's target weight until phase is complete
    return Math.min(totalProgress, phaseInfo.progressWeight);
  };

  // Calculate estimated time remaining
  const calculateEstimatedTimeRemaining = () => {
    const elapsedSeconds = recipeGeneration.elapsedTime;
    
    if (elapsedSeconds > TOTAL_EXPECTED_DURATION) {
      return "Almost done..."; // Don't give false hope when we're over time
    }
    
    const estimatedRemaining = Math.max(0, TOTAL_EXPECTED_DURATION - elapsedSeconds);
    
    if (estimatedRemaining <= 5) {
      return "Just a moment...";
    } else if (estimatedRemaining <= 15) {
      return `~${estimatedRemaining}s remaining`;
    } else {
      const mins = Math.floor(estimatedRemaining / 60);
      const secs = estimatedRemaining % 60;
      if (mins > 0) {
        return `~${mins}:${secs.toString().padStart(2, '0')} remaining`;
      } else {
        return `~${estimatedRemaining}s remaining`;
      }
    }
  };

  const progressPercentage = Math.round(calculateRealisticProgress());
  const estimatedTimeRemaining = calculateEstimatedTimeRemaining();
  const displayMessage = DYNAMIC_MESSAGES[currentMessageIndex];

  // Enhanced styling for mobile standalone mode
  const isMobileStandalone = isMobile && isStandalone;

  // Handle cancel with global state cleanup
  const handleCancel = () => {
    console.log('🚫 Global recipe generation cancelled by user');
    cancelRecipeGeneration();
  };

  const stickyCard = (
    <AnimatePresence>
      {recipeGeneration.showModal && (
        // Full-width container for perfect centering
        <motion.div
          initial={hasAnimated ? { y: 0, opacity: 1 } : { y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={hasAnimated ? { duration: 0 } : { type: "spring", damping: 20, stiffness: 300 }}
          className={`fixed top-0 left-0 right-0 ${
            isMobileStandalone ? 'z-[9999]' : 'z-50'
          }`}
          style={{
            width: '100vw',
            height: 'auto',
            pointerEvents: 'none' // Allow clicks to pass through the container
          }}
        >
          {/* Centered content container */}
          <div 
            className="flex justify-center items-start pt-4"
            style={{
              width: '100%',
              minHeight: 'auto'
            }}
          >
            {/* The actual modal card */}
            <div 
              className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 overflow-hidden"
              style={{
                width: isMobile ? 'calc(100vw - 2rem)' : '420px',
                maxWidth: isMobile ? '380px' : '420px',
                pointerEvents: 'auto' // Re-enable clicks for the modal content
              }}
            >
              {/* Header Section */}
              <div className="flex items-center justify-between p-4 pb-3">
                <div className="flex items-center gap-3">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center text-white shadow-lg"
                  >
                    <ChefHat size={16} />
                  </motion.div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm">
                      AI Recipe Generation
                    </h3>
                    <p className="text-xs text-gray-600">Creating your perfect meal</p>
                  </div>
                </div>
                <button
                  onClick={handleCancel}
                  className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={16} className="text-gray-500" />
                </button>
              </div>

              {/* Progress Section */}
              <div className="px-4 pb-4">
                {/* Current Phase with Dynamic Text Animation */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">{phaseInfo.icon}</span>
                  
                  {/* Dynamic Text Container with Roll-up Animation */}
                  <div className="flex-1 overflow-hidden relative h-5"> {/* Fixed height container */}
                    <AnimatePresence mode="wait">
                      {showMessage && (
                        <motion.span
                          key={currentMessageIndex}
                          initial={hasAnimated ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          exit={{ y: -20, opacity: 0 }}
                          transition={hasAnimated ? { duration: 0 } : { 
                            duration: 0.3,
                            ease: [0.4, 0.0, 0.2, 1] // Custom easing for smooth roll
                          }}
                          className="text-sm font-medium text-gray-800 absolute left-0 top-0"
                        >
                          {displayMessage}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </div>
                  
                  <span className="text-xs text-gray-500">{formatTime(recipeGeneration.elapsedTime)}</span>
                </div>

                {/* Progress Bar */}
                <div className="relative">
                  <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                    <motion.div
                      initial={hasAnimated ? { width: `${progressPercentage}%` } : { width: 0 }}
                      animate={{ width: `${progressPercentage}%` }}
                      transition={hasAnimated ? { duration: 0 } : { duration: 0.5, ease: "easeOut" }}
                      className="h-full bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 rounded-full relative"
                    >
                      {/* Animated shine effect */}
                      <motion.div
                        animate={{ x: [-20, 320] }}
                        transition={{ 
                          duration: 1.5, 
                          repeat: Infinity, 
                          ease: "easeInOut",
                          ...(hasAnimated ? { delay: 0 } : { delay: 0.5 })
                        }}
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent rounded-full"
                        style={{ width: '20px' }}
                      />
                    </motion.div>
                  </div>
                  
                  {/* Progress percentage and estimated time */}
                  <div className="flex justify-between items-center mt-2">
                    <div className="flex items-center gap-1">
                      <Zap size={12} className="text-orange-500" />
                      <span className="text-xs text-gray-600">
                        {recipeGeneration.isActive ? estimatedTimeRemaining : 'Complete'}
                      </span>
                    </div>
                    <span className="text-xs font-medium text-gray-700">{progressPercentage}%</span>
                  </div>
                </div>

                {/* Error Display */}
                {recipeGeneration.error && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-xs text-red-600 font-medium">Error:</p>
                    <p className="text-xs text-red-500">{recipeGeneration.error}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return ReactDOM.createPortal(stickyCard, document.body);
} 